import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CustomIcon } from '../../UI';
import PhotosModal from '../slider/PhotosModal';
import RouteMapModal from '../route-map/RouteMapModal';
import './LoadInfoModal.scss';
import fileService from '../../../services/fileService';

const LoadInfoModal = ({ isOpen, onClose, loadData }) => {
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    shipmentDetails: false,
    pickupDelivery: false,
    carrierInfo: false,
    datesFees: false,
    documents: false,
  });

  const [activeType, setActiveType] = useState('freight'); // 'freight' or 'vehicle'
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxTitle, setLightboxTitle] = useState('');
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isRouteMapOpen, setIsRouteMapOpen] = useState(false);
  const [copiedTracking, setCopiedTracking] = useState(false);
  const accordionRef = useRef(null);
  const [signedUrls, setSignedUrls] = useState({});

  const formatEquipmentSummary = (equipment = []) => {
    if (!Array.isArray(equipment) || equipment.length === 0) return '';
    return equipment
      .map(item => {
        const count = Array.isArray(item.sizes) ? item.sizes.length : 0;
        const label = count === 1 ? 'unit' : 'units';
        return `${item.type} ${count} ${label}`;
      })
      .join(', ');
  };

  // Determine active type based on loadData
  useEffect(() => {
    if (!loadData) return;
    
    const hasVehicle = loadData.type?.vehicle || (loadData.vehicle?.shipment?.length > 0);
    const hasFreight = loadData.type?.freight || (loadData.freight?.shipment?.length > 0);
    
    if (hasVehicle && !hasFreight) {
      setActiveType('vehicle');
    } else if (hasFreight) {
      setActiveType('freight');
    } else if (hasVehicle) {
      setActiveType('vehicle');
    }
  }, [loadData]);

  // Reset expanded sections when modal opens
  useEffect(() => {
    if (isOpen) {
      setExpandedSections({
        overview: true,
        shipmentDetails: false,
        pickupDelivery: false,
        carrierInfo: false,
        datesFees: false,
        documents: false,
      });
      setCopiedTracking(false);
      
      // Scroll to top when modal opens
      setTimeout(() => {
        if (accordionRef.current) {
          accordionRef.current.scrollTop = 0;
        }
      }, 100);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [isOpen, onClose]);

  const load = loadData || {};

  const toggleSection = (section) => {
    const isExpanding = !expandedSections[section];
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));

    // Scroll to section when expanding
    if (isExpanding && accordionRef.current) {
      setTimeout(() => {
        const sectionElement = accordionRef.current?.querySelector(`[data-section="${section}"]`);
        if (sectionElement) {
          sectionElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
  };

  const formatDate = (date) => {
    if (!date) return '--';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDateOnly = (date) => {
    if (!date) return '--';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatAddress = (address) => {
    if (!address) return '--';
    const parts = [];
    if (address.address) parts.push(address.address);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zipCode || address.zip) parts.push(address.zipCode || address.zip);
    return parts.length > 0 ? parts.join(', ') : '--';
  };

  const getStatusColor = (status) => {
    if (!status) return 'status-default';
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'delivered':
        return 'status-delivered';
      case 'picked up':
      case 'pickedup':
      case 'dispatched':
      case 'on hold':
      case 'onhold':
        return 'status-pending';
      case 'cancelled':
      case 'canceled':
        return 'status-cancelled';
      case 'listed':
      default:
        return 'status-default';
    }
  };

  const handleViewImages = (images, title) => {
    if (!images || images.length === 0) return;
    
    const normalizedImages = images.map((img, index) => {
      if (typeof img === 'string') {
        return {
          id: `img-${index}`,
          url: img,
          alt: `${title} ${index + 1}`,
        };
      }
      return {
        id: img.id || `img-${index}`,
        url: img.url || img,
        alt: img.alt || `${title} ${index + 1}`,
      };
    }).filter(img => img.url);

    if (normalizedImages.length > 0) {
      setLightboxImages(normalizedImages);
      setLightboxTitle(title);
      setIsLightboxOpen(true);
    }
  };

  const handleCopyTracking = () => {
    if (loadData.tracking) {
      navigator.clipboard.writeText(loadData.tracking);
      setCopiedTracking(true);
      setTimeout(() => setCopiedTracking(false), 2000);
    }
  };

  

  const normalizeKey = (value) => {
    if (!value || typeof value !== 'string') return null;
    if (value.startsWith('http') || value.startsWith('blob:')) return null;
    return value.replace(/^\/api\/files\//, '').replace(/^\/files\//, '');
  };

  const resolveUrl = (value) => {
    if (!value || typeof value !== 'string') return value;
    if (value.startsWith('http') || value.startsWith('blob:')) return value;
    const key = normalizeKey(value);
    return (key && signedUrls[key]) || (key ? `/files/${key}` : value);
  };

  const collectKeys = useMemo(() => {
    const keys = [];
    const addKeys = (arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((item) => {
        const value = typeof item === 'string' ? item : item?.url || item?.key || item?.file;
        const key = normalizeKey(value);
        if (key) keys.push(key);
      });
    };

    addKeys(load.freight?.freightImages);
    addKeys(load.vehicle?.vehicleImages);
    addKeys(load.pickup?.images);
    addKeys(load.delivery?.images);
    addKeys(load.documents);
    addKeys(load.pdfs);
    return Array.from(new Set(keys));
  }, [load]);

  useEffect(() => {
    let isMounted = true;
    const fetchSignedUrls = async () => {
      if (!collectKeys.length) {
        if (isMounted) setSignedUrls({});
        return;
      }
      const urlMap = await fileService.getSignedUrls(collectKeys);
      if (isMounted) setSignedUrls(urlMap);
    };
    fetchSignedUrls();
    return () => { isMounted = false; };
  }, [collectKeys]);
  const hasVehicle = load.type?.vehicle || (load.vehicle?.shipment?.length > 0);
  const hasFreight = load.type?.freight || (load.freight?.shipment?.length > 0);
  const hasBothTypes = hasVehicle && hasFreight;

  const vehicleShipments = load.vehicle?.shipment || [];
  const freightShipments = load.freight?.shipment || [];
  const currentShipments = activeType === 'vehicle' ? vehicleShipments : freightShipments;

  // Collect all images for gallery
  const allImages = [];
  if (load.freight?.freightImages) {
    allImages.push(...load.freight.freightImages.map((img, i) => ({
      id: `freight-${i}`,
      url: resolveUrl(typeof img === 'string' ? img : img.url || img),
      type: 'freight',
      alt: `Freight image ${i + 1}`,
    })));
  }
  if (load.vehicle?.vehicleImages) {
    allImages.push(...load.vehicle.vehicleImages.map((img, i) => ({
      id: `vehicle-${i}`,
      url: resolveUrl(typeof img === 'string' ? img : img.url || img),
      type: 'vehicle',
      alt: `Vehicle image ${i + 1}`,
    })));
  }
  if (load.pickup?.images) {
    allImages.push(...load.pickup.images.map((img, i) => ({
      id: `pickup-${i}`,
      url: resolveUrl(typeof img === 'string' ? img : img.url || img),
      type: 'pickup',
      alt: `Pickup image ${i + 1}`,
    })));
  }
  if (load.delivery?.images) {
    allImages.push(...load.delivery.images.map((img, i) => ({
      id: `delivery-${i}`,
      url: resolveUrl(typeof img === 'string' ? img : img.url || img),
      type: 'delivery',
      alt: `Delivery image ${i + 1}`,
    })));
  }

  if (!isOpen || !loadData) return null;

  const modalContent = (
    <>
      <div className="load-info-modal-overlay" onClick={onClose}>
        <div className="load-info-modal" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="load-info-modal-header">
            <div className="load-info-modal-header-left">
              <h2 className="load-info-modal-title">Load Information</h2>
              <div className="load-info-modal-ids">
                {load.orderId && (
                  <span className="load-id">Order: {load.orderId}</span>
                )}
                {load.loadId && (
                  <span className="load-id">Load: {load.loadId}</span>
                )}
              </div>
            </div>
            <div className="load-info-modal-header-right">
              {load.status && (
                <span className={`status-badge ${getStatusColor(load.status)}`}>
                  {load.status}
                </span>
              )}
              <button className="close-btn" onClick={onClose}>
                <CustomIcon name="close" size={24} />
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="load-info-modal-body" ref={accordionRef}>
            {/* Overview Section */}
            <div className="accordion-section" data-section="overview">
              <div 
                className="accordion-header"
                onClick={() => toggleSection('overview')}
              >
                <h3 className="accordion-title">Overview</h3>
                <CustomIcon 
                  name={expandedSections.overview ? "up" : "down"} 
                  size={20} 
                  type="svg"
                />
              </div>
              {expandedSections.overview && (
                <div className="accordion-content">
                  <div className="info-grid">
                    {load.customer?.companyName && (
                      <div className="info-item">
                        <span className="info-label">Customer:</span>
                        <span className="info-value">{load.customer.companyName}</span>
                      </div>
                    )}
                    {load.customer?.email && (
                      <div className="info-item">
                        <span className="info-label">Customer Email:</span>
                        <span className="info-value">{load.customer.email}</span>
                      </div>
                    )}
                    {load.customerEmails && load.customerEmails.length > 0 && (
                      <div className="info-item">
                        <span className="info-label">Additional Emails:</span>
                        <span className="info-value">{load.customerEmails.join(', ')}</span>
                      </div>
                    )}
                    {load.customerRate && (
                      <div className="info-item">
                        <span className="info-label">Customer Rate:</span>
                        <span className="info-value">${load.customerRate}</span>
                      </div>
                    )}
                    {/* Show loadCustomerRepresentativePeoples (independent copy for this load) */}
                    {load.loadCustomerRepresentativePeoples && Array.isArray(load.loadCustomerRepresentativePeoples) && load.loadCustomerRepresentativePeoples.length > 0 && (
                      <div className="info-item full-width">
                        <span className="info-label">Representative People (for this load):</span>
                        <div className="people-list" style={{ marginTop: '8px' }}>
                          {load.loadCustomerRepresentativePeoples.map((person, index) => (
                            <div key={index} className="person-item">
                              <div><strong>{person.fullName || 'N/A'}</strong></div>
                              {person.email && <div className="person-item__text">Email: {person.email}</div>}
                              {person.phoneNumber && <div className="person-item__text">Phone: {person.phoneNumber}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {load.carrierRate && (
                      <div className="info-item">
                        <span className="info-label">Carrier Rate:</span>
                        <span className="info-value">${load.carrierRate}</span>
                      </div>
                    )}
                    {load.paymentMethod && (
                      <div className="info-item">
                        <span className="info-label">Payment Method:</span>
                        <span className="info-value">{load.paymentMethod}</span>
                      </div>
                    )}
                    {load.paymentTerms && (
                      <div className="info-item">
                        <span className="info-label">Payment Terms:</span>
                        <span className="info-value">{load.paymentTerms}</span>
                      </div>
                    )}
                    {load.tracking && (
                      <div className="info-item tracking-item">
                        <span className="info-label">Tracking:</span>
                        <div className="tracking-value">
                          <a 
                            href={load.tracking} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="tracking-link"
                          >
                            {load.tracking}
                          </a>
                          <button 
                            className="copy-tracking-btn"
                            onClick={handleCopyTracking}
                            title="Copy tracking link"
                          >
                            <CustomIcon name={copiedTracking ? "check-01" : "copy"} size={16} />
                            {copiedTracking && <span className="copied-text">Copied!</span>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Shipment Details Section */}
            <div className="accordion-section" data-section="shipmentDetails">
              <div 
                className="accordion-header"
                onClick={() => toggleSection('shipmentDetails')}
              >
                <h3 className="accordion-title">Shipment Details</h3>
                <CustomIcon 
                  name={expandedSections.shipmentDetails ? "up" : "down"} 
                  size={20} 
                  type="svg"
                />
              </div>
              {expandedSections.shipmentDetails && (
                <div className="accordion-content">
                  {/* Type Tabs */}
                  {hasBothTypes && (
                    <div className="type-tabs">
                      <button
                        type="button"
                        className={`type-tab ${activeType === 'freight' ? 'active' : ''}`}
                        onClick={() => setActiveType('freight')}
                      >
                        Freight {freightShipments.length > 1 && `(${freightShipments.length})`}
                      </button>
                      <button
                        type="button"
                        className={`type-tab ${activeType === 'vehicle' ? 'active' : ''}`}
                        onClick={() => setActiveType('vehicle')}
                      >
                        Vehicle {vehicleShipments.length > 1 && `(${vehicleShipments.length})`}
                      </button>
                    </div>
                  )}

                  {/* Shipment Details Content */}
                  <div className="shipment-details-content">
                    {activeType === 'freight' && (
                      <div className="shipment-list">
                        {freightShipments.length > 0 ? (
                          freightShipments.map((shipment, index) => (
                            <div key={index} className="shipment-item">
                              <h4 className="shipment-item-title">
                                Freight #{index + 1}
                              </h4>
                              <div className="info-grid">
                                {shipment.description && (
                                  <div className="info-item">
                                    <span className="info-label">Description:</span>
                                    <span className="info-value">{shipment.description}</span>
                                  </div>
                                )}
                                {shipment.quantity && (
                                  <div className="info-item">
                                    <span className="info-label">Quantity:</span>
                                    <span className="info-value">{shipment.quantity}</span>
                                  </div>
                                )}
                                {shipment.weight && (
                                  <div className="info-item">
                                    <span className="info-label">Weight:</span>
                                    <span className="info-value">{shipment.weight} lbs</span>
                                  </div>
                                )}
                                {(shipment.dimensions || (shipment.dimensionsLength && shipment.dimensionsWidth && shipment.dimensionsHeight)) && (
                                  <div className="info-item">
                                    <span className="info-label">Dimensions:</span>
                                    <span className="info-value">
                                      {shipment.dimensions || 
                                        `${shipment.dimensionsLength}" × ${shipment.dimensionsWidth}" × ${shipment.dimensionsHeight}"`}
                                    </span>
                                  </div>
                                )}
                                {shipment.nmfc && (
                                  <div className="info-item">
                                    <span className="info-label">NMFC:</span>
                                    <span className="info-value">{shipment.nmfc}</span>
                                  </div>
                                )}
                                {shipment.class && (
                                  <div className="info-item">
                                    <span className="info-label">Class:</span>
                                    <span className="info-value">{shipment.class}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-data">No freight shipments</div>
                        )}
                      </div>
                    )}

                    {activeType === 'vehicle' && (
                      <div className="shipment-list">
                        {vehicleShipments.length > 0 ? (
                          vehicleShipments.map((shipment, index) => (
                            <div key={index} className="shipment-item">
                              <h4 className="shipment-item-title">
                                Vehicle #{index + 1}
                              </h4>
                              <div className="info-grid">
                                {shipment.vin && (
                                  <div className="info-item">
                                    <span className="info-label">VIN:</span>
                                    <span className="info-value">{shipment.vin}</span>
                                  </div>
                                )}
                                {shipment.year && (
                                  <div className="info-item">
                                    <span className="info-label">Year:</span>
                                    <span className="info-value">{shipment.year}</span>
                                  </div>
                                )}
                                {shipment.make && (
                                  <div className="info-item">
                                    <span className="info-label">Make:</span>
                                    <span className="info-value">{shipment.make}</span>
                                  </div>
                                )}
                                {shipment.model && (
                                  <div className="info-item">
                                    <span className="info-label">Model:</span>
                                    <span className="info-value">{shipment.model}</span>
                                  </div>
                                )}
                                {shipment.condition && (
                                  <div className="info-item">
                                    <span className="info-label">Condition:</span>
                                    <span className="info-value">{shipment.condition}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-data">No vehicle shipments</div>
                        )}
                      </div>
                    )}

                    {/* Special Requirements */}
                    {activeType === 'vehicle' && load.vehicle?.specialRequirements && (
                      <div className="special-requirements">
                        <span className="info-label">Special Requirements:</span>
                        <span className="info-value">{load.vehicle.specialRequirements}</span>
                      </div>
                    )}

                    {/* Image Gallery */}
                    {allImages.length > 0 && (
                      <div className="image-gallery-section">
                        <h4 className="gallery-title">Images</h4>
                        <div className="image-gallery">
                          {allImages.slice(0, 6).map((img, index) => (
                            <div 
                              key={img.id || index} 
                              className="gallery-thumbnail"
                              onClick={() => handleViewImages(allImages, 'All Images')}
                            >
                              <img src={img.url} alt={img.alt} />
                              {index === 5 && allImages.length > 6 && (
                                <div className="more-images-overlay">
                                  +{allImages.length - 6} more
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {allImages.length > 6 && (
                          <button 
                            className="view-all-images-btn"
                            onClick={() => handleViewImages(allImages, 'All Images')}
                          >
                            View All Images ({allImages.length})
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Pickup & Delivery Section */}
            <div className="accordion-section" data-section="pickupDelivery">
              <div 
                className="accordion-header"
                onClick={() => toggleSection('pickupDelivery')}
              >
                <h3 className="accordion-title">Pickup & Delivery</h3>
                <CustomIcon 
                  name={expandedSections.pickupDelivery ? "up" : "down"} 
                  size={20} 
                  type="svg"
                />
              </div>
              {expandedSections.pickupDelivery && (
                <div className="accordion-content">
                  <div className="pickup-delivery-grid">
                    {/* Pickup */}
                    <div className="location-card pickup">
                      <h4 className="location-title">
                        <CustomIcon name="marker-02" size={20} type="svg" />
                        Pickup Location
                      </h4>
                      {load.pickup ? (
                        <>
                          {load.pickup.locationName && (
                            <div className="location-name">{load.pickup.locationName}</div>
                          )}
                          <div className="location-address">
                            {formatAddress(load.pickup.address)}
                          </div>
                          {load.pickup.contactPhone && (
                            <div className="location-contact">
                              <span className="info-label">Phone:</span>
                              <span className="info-value">{load.pickup.contactPhone}</span>
                            </div>
                          )}
                          {load.pickup.date && (
                            <div className="location-date">
                              <span className="info-label">Date:</span>
                              <span className="info-value">{formatDate(load.pickup.date)}</span>
                            </div>
                          )}
                          {load.pickup.notes && (
                            <div className="location-notes">
                              <span className="info-label">Notes:</span>
                              <span className="info-value">{load.pickup.notes}</span>
                            </div>
                          )}
                          {load.pickup.images && load.pickup.images.length > 0 && (
                            <button 
                              className="view-images-btn"
                              onClick={() => handleViewImages(load.pickup.images, 'Pickup Photos')}
                            >
                              View Photos ({load.pickup.images.length})
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="no-data">No pickup information</div>
                      )}
                    </div>

                    {/* Delivery */}
                    <div className="location-card delivery">
                      <h4 className="location-title">
                        <CustomIcon name="marker-02" size={20} type="svg" />
                        Delivery Location
                      </h4>
                      {load.delivery ? (
                        <>
                          {load.delivery.locationName && (
                            <div className="location-name">{load.delivery.locationName}</div>
                          )}
                          <div className="location-address">
                            {formatAddress(load.delivery.address)}
                          </div>
                          {load.delivery.contactPhone && (
                            <div className="location-contact">
                              <span className="info-label">Phone:</span>
                              <span className="info-value">{load.delivery.contactPhone}</span>
                            </div>
                          )}
                          {load.delivery.date && (
                            <div className="location-date">
                              <span className="info-label">Date:</span>
                              <span className="info-value">{formatDate(load.delivery.date)}</span>
                            </div>
                          )}
                          {load.delivery.notes && (
                            <div className="location-notes">
                              <span className="info-label">Notes:</span>
                              <span className="info-value">{load.delivery.notes}</span>
                            </div>
                          )}
                          {load.delivery.images && load.delivery.images.length > 0 && (
                            <button 
                              className="view-images-btn"
                              onClick={() => handleViewImages(load.delivery.images, 'Delivery Photos')}
                            >
                              View Photos ({load.delivery.images.length})
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="no-data">No delivery information</div>
                      )}
                    </div>
                  </div>

                  {/* Route Preview */}
                  {(load.pickup || load.delivery) && (
                    <div className="route-preview-section">
                      <button 
                        className="view-route-btn"
                        onClick={() => setIsRouteMapOpen(true)}
                      >
                        <CustomIcon name="map1" size={24} />
                        View Route Map
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Carrier Info Section */}
            <div className="accordion-section" data-section="carrierInfo">
              <div 
                className="accordion-header"
                onClick={() => toggleSection('carrierInfo')}
              >
                <h3 className="accordion-title">Carrier Info</h3>
                <CustomIcon 
                  name={expandedSections.carrierInfo ? "up" : "down"} 
                  size={20} 
                  type="svg"
                />
              </div>
              {expandedSections.carrierInfo && (
                <div className="accordion-content">
                  {load.carrier ? (
                    <div className="info-grid">
                      {load.carrier.id && (
                        <div className="info-item">
                          <span className="info-label">Carrier ID:</span>
                          <span className="info-value">{load.carrier.id}</span>
                        </div>
                      )}
                      {load.carrier.name && (
                        <div className="info-item">
                          <span className="info-label">Name:</span>
                          <span className="info-value">{load.carrier.name}</span>
                        </div>
                      )}
                      {load.carrier.companyName && (
                        <div className="info-item">
                          <span className="info-label">Company:</span>
                          <span className="info-value">{load.carrier.companyName}</span>
                        </div>
                      )}
                      {load.carrier.phoneNumber && (
                        <div className="info-item">
                          <span className="info-label">Phone:</span>
                          <span className="info-value">{load.carrier.phoneNumber}</span>
                        </div>
                      )}
                      {load.carrier.email && (
                        <div className="info-item">
                          <span className="info-label">Email:</span>
                          <span className="info-value">{load.carrier.email}</span>
                        </div>
                      )}
                      {load.carrierEmails && load.carrierEmails.length > 0 && (
                        <div className="info-item">
                          <span className="info-label">Additional Emails:</span>
                          <span className="info-value">{load.carrierEmails.join(', ')}</span>
                        </div>
                      )}
                      {load.carrier.mcNumber && (
                        <div className="info-item">
                          <span className="info-label">MC#:</span>
                          <span className="info-value">{load.carrier.mcNumber}</span>
                        </div>
                      )}
                      {load.carrier.dotNumber && (
                        <div className="info-item">
                          <span className="info-label">DOT#:</span>
                          <span className="info-value">{load.carrier.dotNumber}</span>
                        </div>
                      )}
                      {load.carrier.address && (
                        <div className="info-item full-width">
                          <span className="info-label">Address:</span>
                          <span className="info-value">{formatAddress(load.carrier.address)}</span>
                        </div>
                      )}
                      {load.carrier.equipment && load.carrier.equipment.length > 0 && (
                        <div className="info-item">
                          <span className="info-label">Equipment:</span>
                          <span className="info-value">{formatEquipmentSummary(load.carrier.equipment)}</span>
                        </div>
                      )}
                      {load.carrier.capabilities && load.carrier.capabilities.length > 0 && (
                        <div className="info-item">
                          <span className="info-label">Capabilities:</span>
                          <span className="info-value">{load.carrier.capabilities.join(', ')}</span>
                        </div>
                      )}
                      {load.carrier.certifications && load.carrier.certifications.length > 0 && (
                        <div className="info-item">
                          <span className="info-label">Certifications:</span>
                          <span className="info-value">{load.carrier.certifications.join(', ')}</span>
                        </div>
                      )}
                      {load.insurance && (
                        <>
                          {load.insurance.type && (
                            <div className="info-item">
                              <span className="info-label">Insurance Type:</span>
                              <span className="info-value">{load.insurance.type}</span>
                            </div>
                          )}
                          {load.insurance.customAmount && (
                            <div className="info-item">
                              <span className="info-label">Insurance Amount:</span>
                              <span className="info-value">${load.insurance.customAmount}</span>
                            </div>
                          )}
                        </>
                      )}
                      {load.carrierPhotos && load.carrierPhotos.length > 0 && (
                        <div className="info-item full-width">
                          <button 
                            className="view-images-btn"
                            onClick={() => handleViewImages(load.carrierPhotos, 'Carrier Photos')}
                          >
                            View Carrier Photos ({load.carrierPhotos.length})
                          </button>
                        </div>
                      )}
                      {/* Show loadCarrierPeople (independent copy for this load) */}
                      {load.loadCarrierPeople && Array.isArray(load.loadCarrierPeople) && load.loadCarrierPeople.length > 0 && (
                        <div className="info-item full-width">
                          <span className="info-label">People (for this load):</span>
                          <div className="people-list" style={{ marginTop: '8px' }}>
                            {load.loadCarrierPeople.map((person, index) => (
                              <div key={index} className="person-item" style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                <div><strong>{person.type === 'driver' ? 'Driver' : 'Dispatcher'}:</strong> {person.fullName || 'N/A'}</div>
                                {person.email && <div style={{ fontSize: '12px', color: '#666' }}>Email: {person.email}</div>}
                                {person.phoneNumber && <div style={{ fontSize: '12px', color: '#666' }}>Phone: {person.phoneNumber}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="no-data">No carrier information</div>
                  )}
                </div>
              )}
            </div>

            {/* Dates & Fees Section */}
            <div className="accordion-section" data-section="datesFees">
              <div 
                className="accordion-header"
                onClick={() => toggleSection('datesFees')}
              >
                <h3 className="accordion-title">Dates & Fees</h3>
                <CustomIcon 
                  name={expandedSections.datesFees ? "up" : "down"} 
                  size={20} 
                  type="svg"
                />
              </div>
              {expandedSections.datesFees && (
                <div className="accordion-content">
                  <div className="dates-fees-grid">
                    {/* Dates */}
                    <div className="dates-section">
                      <h4 className="section-subtitle">Important Dates</h4>
                      <div className="info-grid">
                        {load.dates?.assignedDate && (
                          <div className="info-item">
                            <span className="info-label">Assigned Date:</span>
                            <span className="info-value">{formatDateOnly(load.dates.assignedDate)}</span>
                          </div>
                        )}
                        {load.dates?.pickupDate && (
                          <div className="info-item">
                            <span className="info-label">Pickup Date:</span>
                            <span className="info-value">
                              {formatDateOnly(load.dates.pickupDate)}
                              {load.dates.pickupDateType && ` (${load.dates.pickupDateType})`}
                            </span>
                          </div>
                        )}
                        {load.dates?.deliveryDate && (
                          <div className="info-item">
                            <span className="info-label">Delivery Date:</span>
                            <span className="info-value">
                              {formatDateOnly(load.dates.deliveryDate)}
                              {load.dates.deliveryDateType && ` (${load.dates.deliveryDateType})`}
                            </span>
                          </div>
                        )}
                        {load.dates?.aging && (
                          <div className="info-item">
                            <span className="info-label">Aging:</span>
                            <span className="info-value">{load.dates.aging} days</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Fees */}
                    <div className="fees-section">
                      <h4 className="section-subtitle">Fees</h4>
                      {load.fees && load.fees.length > 0 ? (
                        <div className="fees-list">
                          {load.fees.map((fee, index) => (
                            <div key={index} className="fee-item">
                              <span className="fee-name">{fee.name}:</span>
                              <span className="fee-amount">${fee.amount}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-data">No fees</div>
                      )}

                      {/* TONU */}
                      {load.tonu && load.tonu.enabled && (
                        <div className="tonu-section">
                          <h4 className="section-subtitle">TONU (Truck Ordered Not Used)</h4>
                          <div className="info-grid">
                            {load.tonu.carrierRate && (
                              <div className="info-item">
                                <span className="info-label">Carrier Rate:</span>
                                <span className="info-value">${load.tonu.carrierRate}</span>
                              </div>
                            )}
                            {load.tonu.customerRate && (
                              <div className="info-item">
                                <span className="info-label">Customer Rate:</span>
                                <span className="info-value">${load.tonu.customerRate}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Documents Section */}
            <div className="accordion-section" data-section="documents">
              <div 
                className="accordion-header"
                onClick={() => toggleSection('documents')}
              >
                <h3 className="accordion-title">Documents</h3>
                <CustomIcon 
                  name={expandedSections.documents ? "up" : "down"} 
                  size={20} 
                  type="svg"
                />
              </div>
              {expandedSections.documents && (
                <div className="accordion-content">
                  {load.documents && load.documents.length > 0 ? (
                    <div className="documents-list">
                      {load.documents.map((doc, index) => {
                        const raw = typeof doc === 'string' ? doc : doc?.file || doc?.url || doc?.key;
                        const fileUrl = raw ? resolveUrl(raw) : null;
                        const name =
                          typeof doc === 'string'
                            ? doc.split('/').pop()
                            : doc?.name || doc?.type || `Document ${index + 1}`;
                        return (
                        <div key={index} className="document-item">
                          <CustomIcon name="file" size={20} type="svg" />
                          <div className="document-info">
                            <span className="document-type">{name || 'Document'}</span>
                            {fileUrl && (
                              <a 
                                href={fileUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="document-link"
                              >
                                View File
                              </a>
                            )}
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  ) : (
                    <div className="no-data">No documents</div>
                  )}
                  {load.pdfs && load.pdfs.length > 0 && (
                    <div className="documents-list">
                      <h4 className="section-subtitle">PDF Files</h4>
                      {load.pdfs.map((pdf, index) => {
                        const fileUrl = resolveUrl(typeof pdf === 'string' ? pdf : pdf?.url || pdf?.file || pdf);
                        const name = typeof pdf === 'string' ? pdf.split('/').pop() : `PDF Document ${index + 1}`;
                        return (
                        <div key={index} className="document-item">
                          <CustomIcon name="file" size={20} type="svg" />
                          <div className="document-info">
                            <span className="document-type">{name || `PDF Document ${index + 1}`}</span>
                            {fileUrl && (
                              <a 
                                href={fileUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="document-link"
                              >
                                View PDF
                              </a>
                            )}
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      <PhotosModal
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        photos={lightboxImages}
        title={lightboxTitle}
      />

      {/* Route Map Modal */}
      <RouteMapModal
        isOpen={isRouteMapOpen}
        onClose={() => setIsRouteMapOpen(false)}
        pickup={load.pickup}
        delivery={load.delivery}
      />
    </>
  );

  return createPortal(modalContent, document.body);
};

export default LoadInfoModal;
